import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from '@/shared/api'
import { InvoiceSchema } from '../model/types'

export const invoiceKeys = {
  all: ['invoice'] as const,
  byProject: (projectId: string) => ['invoice', 'project', projectId] as const,
  byCompany: (companyId: string) => ['invoice', 'company', companyId] as const,
  detail: (id: string) => ['invoice', 'detail', id] as const,
}

export function projectInvoicesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: invoiceKeys.byProject(projectId),
    queryFn: async () => {
      const body = await apiRequest<{ data: unknown[] }>({
        method: 'GET',
        url: `/projects/${projectId}/invoices`,
      })
      return z.array(InvoiceSchema).parse(body.data)
    },
  })
}

// Все счета компании (прямые + по проектам). Заказчику бэк отдаёт только прямые
// счета и счета его выданных сделок. Конверт {data} как у project-invoices.
export function companyInvoicesQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: invoiceKeys.byCompany(companyId),
    queryFn: async () => {
      const body = await apiRequest<{ data: unknown[] }>({
        method: 'GET',
        url: `/companies/${companyId}/invoices`,
      })
      return z.array(InvoiceSchema).parse(body.data)
    },
  })
}

// Контур списка счетов: проект или компания. Единый query (одна форма, чтобы
// не плодить union несовместимых queryOptions у потребителя).
export type InvoiceSource =
  | { kind: 'project'; projectId: string }
  | { kind: 'company'; companyId: string }

export function invoicesQueryOptions(source: InvoiceSource) {
  const queryKey =
    source.kind === 'project'
      ? invoiceKeys.byProject(source.projectId)
      : invoiceKeys.byCompany(source.companyId)
  const url =
    source.kind === 'project'
      ? `/projects/${source.projectId}/invoices`
      : `/companies/${source.companyId}/invoices`
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const body = await apiRequest<{ data: unknown[] }>({ method: 'GET', url })
      return z.array(InvoiceSchema).parse(body.data)
    },
  })
}

export function invoiceDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: invoiceKeys.detail(id),
    queryFn: async () =>
      InvoiceSchema.parse(await apiRequest({ method: 'GET', url: `/invoices/${id}` })),
  })
}
